import React from "react";
import themeColors from "../themeColors";

const linkClass =
  "underline underline-offset-2 font-medium transition-colors hover:opacity-80";
const linkStyle = { color: themeColors.warmRed };

function renderInlineChildren(children, keyPrefix = "n") {
  if (!Array.isArray(children)) return null;

  return children.map((child, j) => {
    if (!child) return null;

    /* Strapi link node: { type: "link", url, children: TextNode[] } */
    if (child.type === "link") {
      const href = child.url || "#";
      const isExternal = /^https?:\/\//i.test(href);
      return (
        <a
          key={`${keyPrefix}-link-${j}`}
          href={href}
          className={linkClass}
          style={linkStyle}
          {...(isExternal
            ? {
                target: child.target || "_blank",
                rel: child.rel || "noopener noreferrer",
              }
            : {})}
        >
          {renderInlineChildren(child.children, `${keyPrefix}-l${j}`)}
        </a>
      );
    }

    /* plain / styled text */
    if (typeof child.text !== "string") return null;

    const segments = child.text.split("\n");
    const classNames = [];
    if (child.bold) classNames.push("font-bold");
    if (child.italic) classNames.push("italic");
    if (child.underline) classNames.push("underline");
    if (child.strikethrough) classNames.push("line-through");
    if (child.code) classNames.push("font-mono text-sm bg-black/5 px-1 rounded");

    return segments.map((segment, k) => (
      <React.Fragment key={`${keyPrefix}-${j}-${k}`}>
        <span className={classNames.join(" ") || undefined}>{segment}</span>
        {k < segments.length - 1 && <br />}
      </React.Fragment>
    ));
  });
}

/**
 * Render Strapi Blocks (rich text) JSON as React nodes.
 * Supports paragraphs, lists, and inline links / bold / italic.
 */
export function renderStrapiBlocks(blocks) {
  if (!Array.isArray(blocks)) return null;

  return blocks.map((block, i) => {
    if (block.type === "list") {
      const isOrdered = block.format === "ordered";
      const ListTag = isOrdered ? "ol" : "ul";
      return (
        <ListTag
          key={i}
          className={`mb-4 pl-6 ${
            isOrdered ? "list-decimal" : "list-disc"
          } text-gray-700 leading-relaxed`}
        >
          {block.children?.map((li, idx) => (
            <li key={idx}>{renderInlineChildren(li.children, `li${i}-${idx}`)}</li>
          ))}
        </ListTag>
      );
    }

    if (block.type === "heading") {
      const level = Math.min(Math.max(block.level || 2, 1), 4);
      const Tag = `h${level}`;
      const size =
        level === 1
          ? "text-3xl"
          : level === 2
            ? "text-2xl"
            : level === 3
              ? "text-xl"
              : "text-lg";
      return (
        <Tag
          key={i}
          className={`mb-3 mt-6 font-bold text-gray-900 leading-snug ${size}`}
        >
          {renderInlineChildren(block.children, `h${i}`)}
        </Tag>
      );
    }

    if (block.type === "quote") {
      return (
        <blockquote
          key={i}
          className="mb-4 border-l-4 pl-4 italic text-gray-600 leading-relaxed"
          style={{ borderColor: themeColors.warmRed }}
        >
          {renderInlineChildren(block.children, `q${i}`)}
        </blockquote>
      );
    }

    // default: paragraph (and unknown block types with children)
    return (
      <p key={i} className="mb-4 text-gray-700 leading-relaxed">
        {renderInlineChildren(block.children, `p${i}`)}
      </p>
    );
  });
}
